export const ACTIVE_ROLES = {
  CREATIVE: 'creative',
  HOST: 'host',
};

export const ACTIVE_ROLE_PRIVATE_DATA_KEY = 'activeRole';
export const SIGNUP_ROLE_PRIVATE_DATA_KEY = 'signupRole';
export const ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY = 'activeRoleSource';
export const HOST_ROLE_ACTIVATED_PRIVATE_DATA_KEY = 'hostRoleActivated';

export const ACTIVE_ROLE_SOURCES = {
  SIGNUP: 'signup',
  MANUAL: 'manual',
  FALLBACK: 'fallback',
};

export const HOST_ONBOARDING_ROUTE_NAME = 'HostOnboardingPage';
export const CREATIVE_SWITCH_ROUTE_NAME = 'LandingPage';
export const HOST_SWITCH_ROUTE_NAME = 'ManageListingsPage';
export const HOST_CREATE_LISTING_ROUTE_NAME = 'NewListingPage';

export const isValidActiveRole = role =>
  role === ACTIVE_ROLES.CREATIVE || role === ACTIVE_ROLES.HOST;

export const isValidActiveRoleSource = source =>
  Object.values(ACTIVE_ROLE_SOURCES).includes(source);

export const getPrivateData = user =>
  user?.attributes?.profile?.privateData || user?.privateData || {};
export const getPublicData = user =>
  user?.attributes?.profile?.publicData || user?.publicData || { userType: user?.userType };
export const getProtectedData = user =>
  user?.attributes?.profile?.protectedData || user?.protectedData || {};

export const normalizeUserTypeToActiveRole = userType => {
  return userType === ACTIVE_ROLES.HOST ? ACTIVE_ROLES.HOST : ACTIVE_ROLES.CREATIVE;
};

const getConfiguredAvailableRoles = user => {
  const privateData = getPrivateData(user);
  const protectedData = getProtectedData(user);
  const publicData = getPublicData(user);
  const configuredRoles = [
    privateData.availableRoles,
    protectedData.availableRoles,
    publicData.availableRoles,
    privateData.roles,
    protectedData.roles,
    publicData.roles,
  ].find(Array.isArray);

  return configuredRoles ? [...new Set(configuredRoles.filter(isValidActiveRole))] : [];
};

export const resolveOriginalSignupRole = user => {
  const privateData = getPrivateData(user);
  const protectedData = getProtectedData(user);
  const publicData = getPublicData(user);
  const candidates = [
    privateData[SIGNUP_ROLE_PRIVATE_DATA_KEY],
    protectedData[SIGNUP_ROLE_PRIVATE_DATA_KEY],
    publicData[SIGNUP_ROLE_PRIVATE_DATA_KEY],
    privateData.originalSignupRole,
    protectedData.originalSignupRole,
    publicData.originalSignupRole,
    publicData.userType,
    user?.userType,
  ];

  return candidates.find(isValidActiveRole) || null;
};

export const resolveOnlyAvailableRole = user => {
  const availableRoles = getConfiguredAvailableRoles(user);
  return availableRoles.length === 1 ? availableRoles[0] : null;
};

const hasLegacyHostRoleEvidence = user => {
  const privateData = getPrivateData(user);
  const publicData = getPublicData(user);
  const payoutDetails = privateData.payoutDetails || {};

  return Boolean(
    publicData.omiseRecipientId || payoutDetails.recipientId || user?.stripeAccount?.id
  );
};

const hasLegacyManualRoleSelection = user => {
  const privateData = getPrivateData(user);
  const persistedRole = privateData[ACTIVE_ROLE_PRIVATE_DATA_KEY];
  const persistedSource = privateData[ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY];
  const signupRole = resolveOriginalSignupRole(user);

  if (
    isValidActiveRoleSource(persistedSource) ||
    !isValidActiveRole(persistedRole) ||
    !isValidActiveRole(signupRole) ||
    persistedRole === signupRole
  ) {
    return false;
  }

  // Before role provenance was stored, switching a Host to Creative left no separate marker.
  // A Creative acting as Host may still have durable payout-account evidence.
  return (
    persistedRole === ACTIVE_ROLES.CREATIVE ||
    (persistedRole === ACTIVE_ROLES.HOST && hasLegacyHostRoleEvidence(user))
  );
};

export const resolveActiveRoleSource = user => {
  const privateData = getPrivateData(user);
  const persistedSource = privateData[ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY];

  if (isValidActiveRoleSource(persistedSource)) {
    return persistedSource;
  }

  if (hasLegacyManualRoleSelection(user)) {
    return ACTIVE_ROLE_SOURCES.MANUAL;
  }

  return resolveOriginalSignupRole(user)
    ? ACTIVE_ROLE_SOURCES.SIGNUP
    : ACTIVE_ROLE_SOURCES.FALLBACK;
};

export const resolveActiveRole = user => {
  const privateData = getPrivateData(user);
  const persistedRole = privateData[ACTIVE_ROLE_PRIVATE_DATA_KEY];
  const persistedSource = privateData[ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY];

  if (
    isValidActiveRole(persistedRole) &&
    (persistedSource === ACTIVE_ROLE_SOURCES.MANUAL || hasLegacyManualRoleSelection(user))
  ) {
    return persistedRole;
  }

  const signupRole = resolveOriginalSignupRole(user);
  if (signupRole) {
    return signupRole;
  }

  const onlyAvailableRole = resolveOnlyAvailableRole(user);
  if (onlyAvailableRole) {
    return onlyAvailableRole;
  }

  if (isValidActiveRole(persistedRole)) {
    return persistedRole;
  }

  if (hasLegacyHostRoleEvidence(user)) {
    return ACTIVE_ROLES.HOST;
  }

  return normalizeUserTypeToActiveRole(getPublicData(user).userType);
};

export const shouldPersistResolvedActiveRole = user => {
  if (!user?.id) {
    return false;
  }

  const privateData = getPrivateData(user);
  const hasPersistedActiveRole = isValidActiveRole(privateData[ACTIVE_ROLE_PRIVATE_DATA_KEY]);
  const hasPersistedSignupRole = isValidActiveRole(privateData[SIGNUP_ROLE_PRIVATE_DATA_KEY]);
  const hasResolvableSignupRole = isValidActiveRole(resolveOriginalSignupRole(user));
  const hasOnlyAvailableRole = isValidActiveRole(resolveOnlyAvailableRole(user));
  const hasReliableInitialRole =
    hasResolvableSignupRole || hasOnlyAvailableRole || hasLegacyHostRoleEvidence(user);
  const hasPersistedRoleSource = isValidActiveRoleSource(
    privateData[ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY]
  );
  const resolvedRoleDiffers = resolveActiveRole(user) !== privateData[ACTIVE_ROLE_PRIVATE_DATA_KEY];

  return (
    (!hasPersistedActiveRole && hasReliableInitialRole) ||
    (!hasPersistedSignupRole && hasResolvableSignupRole) ||
    (!hasPersistedRoleSource && (hasReliableInitialRole || hasPersistedActiveRole)) ||
    (resolvedRoleDiffers && (hasReliableInitialRole || hasPersistedActiveRole))
  );
};

export const getInitialRolePrivateData = userType => {
  const signupRole = normalizeUserTypeToActiveRole(userType);
  return {
    [SIGNUP_ROLE_PRIVATE_DATA_KEY]: signupRole,
    [ACTIVE_ROLE_PRIVATE_DATA_KEY]: signupRole,
    [ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY]: ACTIVE_ROLE_SOURCES.SIGNUP,
  };
};

export const getActiveRoleUpdatePayload = (
  user,
  activeRole,
  { source = ACTIVE_ROLE_SOURCES.MANUAL, markHostActivated = true } = {}
) => {
  const privateData = getPrivateData(user);
  const signupRole = resolveOriginalSignupRole(user);
  const resolvedActiveRole = isValidActiveRole(activeRole) ? activeRole : ACTIVE_ROLES.CREATIVE;
  const resolvedSource = isValidActiveRoleSource(source) ? source : ACTIVE_ROLE_SOURCES.MANUAL;
  const shouldMarkHostActivated =
    markHostActivated &&
    resolvedSource === ACTIVE_ROLE_SOURCES.MANUAL &&
    resolvedActiveRole === ACTIVE_ROLES.HOST;

  return {
    privateData: {
      ...privateData,
      ...(isValidActiveRole(privateData[SIGNUP_ROLE_PRIVATE_DATA_KEY])
        ? {}
        : signupRole
        ? { [SIGNUP_ROLE_PRIVATE_DATA_KEY]: signupRole }
        : {}),
      ...(shouldMarkHostActivated ? { [HOST_ROLE_ACTIVATED_PRIVATE_DATA_KEY]: true } : {}),
      [ACTIVE_ROLE_PRIVATE_DATA_KEY]: resolvedActiveRole,
      [ACTIVE_ROLE_SOURCE_PRIVATE_DATA_KEY]: resolvedSource,
    },
  };
};

export const getResolvedActiveRoleUpdatePayload = user =>
  getActiveRoleUpdatePayload(user, resolveActiveRole(user), {
    source: resolveActiveRoleSource(user),
    markHostActivated: false,
  });

export const resolveRoleValue = role => (isValidActiveRole(role) ? role : ACTIVE_ROLES.CREATIVE);

export const getOppositeActiveRole = activeRole =>
  resolveRoleValue(activeRole) === ACTIVE_ROLES.HOST ? ACTIVE_ROLES.CREATIVE : ACTIVE_ROLES.HOST;

export const isActiveRoleHost = userOrRole =>
  typeof userOrRole === 'string'
    ? resolveRoleValue(userOrRole) === ACTIVE_ROLES.HOST
    : resolveActiveRole(userOrRole) === ACTIVE_ROLES.HOST;

export const isActiveRoleCreative = userOrRole => !isActiveRoleHost(userOrRole);

export const getActiveRoleLabel = activeRole =>
  resolveRoleValue(activeRole) === ACTIVE_ROLES.HOST ? 'HOST' : 'CREATIVE';

export const hasHostPhoneNumber = currentUser => {
  const phoneNumber = getProtectedData(currentUser).phoneNumber;
  return typeof phoneNumber === 'string' && phoneNumber.trim().length > 0;
};

export const hasHostPayoutDetails = currentUser => {
  const publicData = getPublicData(currentUser);
  const payoutDetails = getPrivateData(currentUser).payoutDetails || {};
  const storedRecipientId = publicData.omiseRecipientId || payoutDetails.recipientId;
  const isInvalid = payoutDetails.recipientStatus === 'invalid';

  return Boolean(storedRecipientId && !isInvalid);
};

export const hasHostSetup = ({ currentUser } = {}) => {
  return hasHostPhoneNumber(currentUser) && hasHostPayoutDetails(currentUser);
};

export const hasActivatedHostRole = ({ currentUser, currentUserHasListings } = {}) => {
  const privateData = getPrivateData(currentUser);
  const originalSignupRole = resolveOriginalSignupRole(currentUser);

  return (
    privateData[HOST_ROLE_ACTIVATED_PRIVATE_DATA_KEY] === true ||
    originalSignupRole === ACTIVE_ROLES.HOST ||
    currentUserHasListings === true ||
    hasHostPayoutDetails(currentUser) ||
    Boolean(currentUser?.stripeAccount?.id)
  );
};

export const getPostSwitchRoute = ({ nextRole, currentUser, currentUserHasListings } = {}) => {
  const role = resolveRoleValue(nextRole);

  if (role === ACTIVE_ROLES.CREATIVE) {
    return {
      name: CREATIVE_SWITCH_ROUTE_NAME,
      params: {},
    };
  }

  if (hasActivatedHostRole({ currentUser, currentUserHasListings })) {
    return {
      name: HOST_SWITCH_ROUTE_NAME,
      params: {},
    };
  }

  return {
    name: HOST_CREATE_LISTING_ROUTE_NAME,
    params: {},
  };
};
